// src/components/Gallery.js
import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useLocation, Link } from 'react-router-dom';
import { FaSpinner } from 'react-icons/fa'; // Import spinner icon

// Utility function to format time as "X time ago"
const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const seconds = Math.floor((now - date) / 1000);

  if (seconds < 60) return `${seconds} seconds ago`;

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} minutes ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hours ago`;

  const days = Math.floor(hours / 24);
  if (days < 30) return `${days} days ago`;

  const months = Math.floor(days / 30);
  if (months < 12) return `${months} months ago`;

  const years = Math.floor(months / 12);
  return `${years} years ago`;
};

// Define categories for display names (must match slugs in LandingPage and ImageUpload)
const categoryDisplayNames = {
  'interior-painting': 'Interior Painting',
  'exterior-painting': 'Exterior Painting',
  'protective-coatings': 'Protective Coatings',
  'color-consultation-matching': 'Color Consultation & Matching',
  'wall-repair': 'Wall Repair & Surface Preparation', // Combined display name
  'ceiling-designing': 'Ceiling Designing',
};

const Gallery = () => {
  const [images, setImages] = useState([]);
  const [selectedImage, setSelectedImage] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [fetchError, setFetchError] = useState('');
  const [page, setPage] = useState(1); // Current page for pagination
  const [hasMore, setHasMore] = useState(true); // Whether there are more images to load
  const LIMIT = 12; // Number of images to load per request

  const API_BASE = process.env.REACT_APP_API_URL;

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const categoryFilter = queryParams.get('category');

  // Function to fetch gallery images with pagination
  const fetchGalleryImages = useCallback(async (reset = false) => {
    setLoading(true);
    setFetchError('');

    const currentPage = reset ? 1 : page;
    let url = `${API_BASE}/api/images?limit=${LIMIT}&skip=${(currentPage - 1) * LIMIT}`;
    if (categoryFilter) {
      url += `&category=${categoryFilter}`;
    }

    console.log(`Gallery: Fetching images for page ${currentPage}, category ${categoryFilter || 'all'}`);

    try {
      const res = await axios.get(url);
      const newImages = res.data;

      if (reset) {
        setImages(newImages);
        setPage(1); // Reset page to 1
      } else {
        setImages(prevImages => [...prevImages, ...newImages]);
      }

      setHasMore(newImages.length === LIMIT); // If we got less than LIMIT, there are no more
      console.log('Gallery: Fetched images successfully. Count:', newImages.length, 'Has More:', newImages.length === LIMIT);
    } catch (err) {
      console.error('Error fetching images for gallery:', err.response?.data || err.message);
      setFetchError('Failed to load images. Please try refreshing.');
      setImages([]);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, [API_BASE, categoryFilter, page]);

  // Initial fetch when component mounts or categoryFilter changes
  useEffect(() => {
    fetchGalleryImages(true); // Pass true to reset state for new category/initial load
  }, [fetchGalleryImages, categoryFilter]); // Depend on categoryFilter to refetch when it changes

  // Handler for "Load More" button
  const handleLoadMore = () => {
    setPage(prevPage => prevPage + 1);
  };

  const handleRefreshGallery = () => {
    fetchGalleryImages(true); // Refresh and reset pagination
  };

  const openModal = (image) => {
    setSelectedImage(image);
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setSelectedImage(null);
  };

  return (
    <div className="min-h-screen bg-gray-100 p-4">
      <h2 className="text-center text-3xl font-bold mb-6 text-gray-800">
        Our Work Gallery
        {categoryFilter && (
          <span className="block text-xl text-cyan-600 mt-2">
            ({categoryDisplayNames[categoryFilter] || categoryFilter.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase())})
          </span>
        )}
      </h2>

      <div className="flex justify-center gap-4 mb-4">
        <button
          onClick={handleRefreshGallery}
          className="bg-cyan-600 hover:bg-cyan-700 text-white py-2 px-4 rounded-full font-semibold"
        >
          Refresh Gallery
        </button>
        {categoryFilter && (
          <Link
            to="/gallery"
            className="bg-gray-600 hover:bg-gray-700 text-white py-2 px-4 rounded-full font-semibold"
          >
            View All
          </Link>
        )}
      </div>

      {fetchError && (
        <p className="text-center text-red-600 mb-4">{fetchError}</p>
      )}

      {loading && images.length === 0 ? ( // Only show full loading spinner if no images are loaded yet
        <div className="flex flex-col items-center justify-center h-64 text-gray-600">
          <FaSpinner className="animate-spin text-cyan-600 text-4xl mb-3" />
          <p>Loading images...</p>
        </div>
      ) : images.length === 0 ? ( // If no images and not loading (e.g., after an error or truly empty)
        <p className="text-center text-gray-600">
          No images found for this category yet.
          {categoryFilter && <Link to="/gallery" className="text-cyan-600 underline ml-1">View all images.</Link>}
        </p>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
          {images.map((img) => (
            <div
              key={img._id}
              className="relative overflow-hidden rounded-lg shadow-md group cursor-pointer h-56 bg-center bg-no-repeat"
              style={{ backgroundImage: `url(${img.url})`, backgroundSize: 'cover' }}
              onClick={() => openModal(img)}
            >
              <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-75 transition-opacity duration-300 flex flex-col justify-end p-4">
                {img.createdAt && (
                  <p className="text-gray-300 text-sm">
                    Uploaded: {formatTimeAgo(img.createdAt)}
                  </p>
                )}
              </div>
            </div>
          ))}
        </div>
      )}

      {hasMore && !loading && images.length > 0 && ( // Show Load More only if there are more and not loading
        <div className="flex justify-center mt-8">
          <button
            onClick={handleLoadMore}
            className="bg-blue-600 hover:bg-blue-700 text-white py-2 px-6 rounded-full font-semibold"
          >
            Load More
          </button>
        </div>
      )}
       {loading && images.length > 0 && ( // Show small spinner when loading more
        <div className="flex justify-center mt-8">
          <FaSpinner className="animate-spin text-cyan-600 text-2xl" />
        </div>
      )}


      {/* Image Modal/Lightbox */}
      {isModalOpen && selectedImage && (
        <div
          className="fixed inset-0 bg-black bg-opacity-75 flex justify-center items-center z-50 p-4"
          onClick={closeModal}
        >
          <div className="relative max-w-4xl max-h-full w-full bg-white rounded-lg overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <button
              onClick={closeModal}
              className="absolute top-2 right-2 text-white text-3xl font-bold bg-gray-800 rounded-full w-10 h-10 flex items-center justify-center hover:bg-gray-700 z-10"
            >
              &times;
            </button>
            <img
              src={selectedImage.url}
              alt={selectedImage.title || 'Enlarged image'}
              className="max-w-full max-h-screen object-contain mx-auto"
              style={{ maxHeight: '90vh' }}
            />
            {selectedImage.createdAt && (
              <div className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black to-transparent p-4 text-white">
                <p className="text-gray-300 text-sm">
                  Uploaded: {formatTimeAgo(selectedImage.createdAt)}
                </p>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default Gallery;